/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { OpenAPIHousien } from '@services/requests/core/OpenAPI';
import { request as __request } from '@services/requests/core/request';
import type { CancelablePromise } from '@services/requests/core/CancelablePromise';
import type {
  CreateLaunchInput,
  Launch,
  LaunchBoard,
  LaunchBoardUnit,
  LaunchDashboard,
  LaunchMaterial,
  LaunchMaterialsResponse,
  MaterialCategory,
  PriceTableOverview,
  PriceTableVersion,
  ResolvedLink,
  UpdateLaunchInput,
} from '../types';

/**
 * Thin HTTP client for Controle de Lançamento endpoints.
 */
export class LaunchesService {
  /** List launches for an agency. */
  public static list(agencyId: string): CancelablePromise<Launch[]> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/launches',
      path: { agencyId },
    });
  }

  /** Get a single launch. */
  public static get(
    agencyId: string,
    launchId: string,
  ): CancelablePromise<Launch> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/launches/{launchId}',
      path: { agencyId, launchId },
    });
  }

  /** Create a draft launch with units. */
  public static create(
    agencyId: string,
    body: CreateLaunchInput,
  ): CancelablePromise<Launch> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/launches',
      path: { agencyId },
      body,
      mediaType: 'application/json',
    });
  }

  /** Update launch metadata. */
  public static update(
    agencyId: string,
    launchId: string,
    body: UpdateLaunchInput,
  ): CancelablePromise<Launch> {
    return __request(OpenAPIHousien, {
      method: 'PATCH',
      url: '/agency/{agencyId}/launches/{launchId}',
      path: { agencyId, launchId },
      body,
      mediaType: 'application/json',
    });
  }

  /** Open a draft launch. */
  public static open(
    agencyId: string,
    launchId: string,
  ): CancelablePromise<Launch> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/launches/{launchId}/open',
      path: { agencyId, launchId },
      body: {},
    });
  }

  /** Close an open launch. */
  public static close(
    agencyId: string,
    launchId: string,
  ): CancelablePromise<Launch> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/launches/{launchId}/close',
      path: { agencyId, launchId },
      body: {},
    });
  }

  /** Append property-linked units to a launch. */
  public static addUnits(
    agencyId: string,
    launchId: string,
    units: CreateLaunchInput['units'],
  ): CancelablePromise<Launch> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/launches/{launchId}/units',
      path: { agencyId, launchId },
      body: { units },
      mediaType: 'application/json',
    });
  }

  /** Remove an available unit from a launch. */
  public static removeUnit(
    agencyId: string,
    launchId: string,
    unitId: string,
  ): CancelablePromise<Launch> {
    return __request(OpenAPIHousien, {
      method: 'DELETE',
      url: '/agency/{agencyId}/launches/{launchId}/units/{unitId}',
      path: { agencyId, launchId, unitId },
    });
  }

  /** Live allocation board snapshot. */
  public static board(
    agencyId: string,
    launchId: string,
  ): CancelablePromise<LaunchBoard> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/launches/{launchId}/board',
      path: { agencyId, launchId },
    });
  }

  /** Hold a free unit for a client. */
  public static hold(
    agencyId: string,
    launchId: string,
    unitId: string,
    clientId: string,
  ): CancelablePromise<LaunchBoardUnit> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/launches/{launchId}/units/{unitId}/hold',
      path: { agencyId, launchId, unitId },
      body: { client_id: clientId },
      mediaType: 'application/json',
    });
  }

  /** Self-release a hold owned by the current broker. */
  public static selfRelease(
    agencyId: string,
    launchId: string,
    unitId: string,
  ): CancelablePromise<LaunchBoardUnit> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/launches/{launchId}/units/{unitId}/self-release',
      path: { agencyId, launchId, unitId },
    });
  }

  /** Confirm deposit on a held unit. */
  public static confirmDeposit(
    agencyId: string,
    launchId: string,
    unitId: string,
  ): CancelablePromise<LaunchBoardUnit> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/launches/{launchId}/units/{unitId}/confirm-deposit',
      path: { agencyId, launchId, unitId },
    });
  }

  /** Mark a reserved unit as sold. */
  public static markSold(
    agencyId: string,
    launchId: string,
    unitId: string,
  ): CancelablePromise<LaunchBoardUnit> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/launches/{launchId}/units/{unitId}/mark-sold',
      path: { agencyId, launchId, unitId },
    });
  }

  /** Force-release a held or reserved unit. */
  public static forceRelease(
    agencyId: string,
    launchId: string,
    unitId: string,
  ): CancelablePromise<LaunchBoardUnit> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/launches/{launchId}/units/{unitId}/force-release',
      path: { agencyId, launchId, unitId },
    });
  }

  /** Revert a sold unit to available (admin/owner only). */
  public static revertSold(
    agencyId: string,
    launchId: string,
    unitId: string,
  ): CancelablePromise<LaunchBoardUnit> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/launches/{launchId}/units/{unitId}/revert-sold',
      path: { agencyId, launchId, unitId },
    });
  }

  /** Aggregated dashboard for all launches. */
  public static dashboard(agencyId: string): CancelablePromise<LaunchDashboard> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/launches/dashboard',
      path: { agencyId },
    });
  }

  /** Aggregated dashboard scoped to a single launch. */
  public static launchDashboard(agencyId: string, launchId: string): CancelablePromise<LaunchDashboard> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/launches/{launchId}/dashboard',
      path: { agencyId, launchId },
    });
  }

  /** List a launch's sales materials, optionally filtered by category. */
  public static materials(
    agencyId: string,
    launchId: string,
    category?: string,
  ): CancelablePromise<LaunchMaterialsResponse> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/launches/{launchId}/materials',
      path: { agencyId, launchId },
      query: category ? { category } : undefined,
    });
  }

  /** Fixed categories plus the ones this agency created. */
  public static materialCategories(
    agencyId: string,
  ): CancelablePromise<MaterialCategory[]> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/launches/-/material-categories',
      path: { agencyId },
    });
  }

  /** Create a category owned by the agency. */
  public static createMaterialCategory(
    agencyId: string,
    label: string,
  ): CancelablePromise<MaterialCategory> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/launches/-/material-categories',
      path: { agencyId },
      body: { label },
      mediaType: 'application/json',
    });
  }

  /**
   * Recognise a pasted link without storing anything.
   *
   * Always resolves: the outcome travels in `embed_status`, because a
   * rejected promise here would surface as the blank frame the design
   * explicitly forbids.
   */
  public static resolveLink(
    agencyId: string,
    launchId: string,
    url: string,
  ): CancelablePromise<ResolvedLink> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/launches/{launchId}/materials/resolve-link',
      path: { agencyId, launchId },
      body: { url },
      mediaType: 'application/json',
    });
  }

  /** Create an embedded material from an already-resolved link. */
  public static createEmbedMaterial(
    agencyId: string,
    launchId: string,
    body: { url: string; category: string; title: string; typology_keys?: string[] },
  ): CancelablePromise<LaunchMaterial> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/launches/{launchId}/materials',
      path: { agencyId, launchId },
      body,
      mediaType: 'application/json',
    });
  }

  /** Register a hosted material and get its presigned upload target. */
  public static materialUploadLink(
    agencyId: string,
    launchId: string,
    body: {
      filename: string;
      content_type: string;
      category: string;
      title: string;
      typology_keys?: string[];
    },
  ): CancelablePromise<{ material: LaunchMaterial; upload: Record<string, unknown> }> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/launches/{launchId}/materials/upload-link',
      path: { agencyId, launchId },
      body,
      mediaType: 'application/json',
    });
  }

  /** Rename a material, or move it to another category. */
  public static updateMaterial(
    agencyId: string,
    launchId: string,
    materialId: string,
    body: { title?: string; category?: string; typology_keys?: string[] },
  ): CancelablePromise<LaunchMaterial> {
    return __request(OpenAPIHousien, {
      method: 'PATCH',
      url: '/agency/{agencyId}/launches/{launchId}/materials/{materialId}',
      path: { agencyId, launchId, materialId },
      body,
      mediaType: 'application/json',
    });
  }

  /** Delete a material and its bytes. */
  public static deleteMaterial(
    agencyId: string,
    launchId: string,
    materialId: string,
  ): CancelablePromise<void> {
    return __request(OpenAPIHousien, {
      method: 'DELETE',
      url: '/agency/{agencyId}/launches/{launchId}/materials/{materialId}',
      path: { agencyId, launchId, materialId },
    });
  }

  /** Current price-table version, its archive, and this user's read mark. */
  public static priceTables(
    agencyId: string,
    launchId: string,
  ): CancelablePromise<PriceTableOverview> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agency/{agencyId}/launches/{launchId}/price-tables',
      path: { agencyId, launchId },
    });
  }

  /** Record that the acting user opened this version. */
  public static markPriceTableSeen(
    agencyId: string,
    launchId: string,
    version: number,
  ): CancelablePromise<void> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/launches/{launchId}/price-tables/{version}/seen',
      path: { agencyId, launchId, version },
    });
  }

  /** Register a deposit with its receipt, pending a manager's review. */
  public static submitDeposit(
    agencyId: string, launchId: string, unitId: string,
    body: { client_id: string; proof_key: string },
  ): CancelablePromise<LaunchBoardUnit> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/launches/{launchId}/units/{unitId}/submit-deposit',
      path: { agencyId, launchId, unitId },
      body,
      mediaType: 'application/json',
    });
  }

  /** Accept a pending deposit. */
  public static validateDeposit(
    agencyId: string, launchId: string, unitId: string,
  ): CancelablePromise<LaunchBoardUnit> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/launches/{launchId}/units/{unitId}/validate-deposit',
      path: { agencyId, launchId, unitId },
    });
  }

  /** Refuse a pending deposit and return the unit to the market. */
  public static rejectDeposit(
    agencyId: string, launchId: string, unitId: string, reason: string,
  ): CancelablePromise<LaunchBoardUnit> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/launches/{launchId}/units/{unitId}/reject-deposit',
      path: { agencyId, launchId, unitId },
      body: { reason },
      mediaType: 'application/json',
    });
  }

  /** Publish a material as the launch's next price-table version. */
  public static publishPriceTable(
    agencyId: string,
    launchId: string,
    materialId: string,
  ): CancelablePromise<PriceTableVersion> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agency/{agencyId}/launches/{launchId}/price-tables',
      path: { agencyId, launchId },
      body: { material_id: materialId },
      mediaType: 'application/json',
    });
  }
}

